import type { AssetType } from '@/api/types';

// ─── API ──────────────────────────────────────────────────────────────────────

// NOTE: The canonical API_BASE_URL is in api/client.ts. Use apiClient for
// all requests rather than constructing URLs from this constant.
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api';

// ─── Map ──────────────────────────────────────────────────────────────────────

/** Default center: US Midwest (Iowa) */
export const MAP_DEFAULT_CENTER: [number, number] = [-93.5, 41.5];
export const MAP_DEFAULT_ZOOM = 6;
export const MAP_MIN_ZOOM = 3;
export const MAP_MAX_ZOOM = 20;

/** MapLibre style — open aerial imagery via ESRI */
export const MAP_STYLE_SATELLITE =
  'https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json';

export const MAP_STYLE_LIGHT =
  'https://basemaps.cartocdn.com/gl/positron-gl-style/style.json';

export const MAP_STYLE_TERRAIN =
  'https://basemaps.cartocdn.com/gl/voyager-gl-style/style.json';

// ─── Responsive Breakpoints (px) ─────────────────────────────────────────────

export const BREAKPOINTS = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
} as const;

export type Breakpoint = keyof typeof BREAKPOINTS;

// ─── Date / Time ──────────────────────────────────────────────────────────────

export const DATE_FORMAT = 'MMM d, yyyy';
export const DATE_TIME_FORMAT = 'MMM d, yyyy h:mm a';
export const ISO_DATE_FORMAT = 'yyyy-MM-dd';

// ─── Vegetation Index Labels ──────────────────────────────────────────────────

export const VEGETATION_INDEX_LABELS: Record<string, string> = {
  ndvi: 'NDVI',
  evi: 'EVI',
  savi: 'SAVI',
  ndwi: 'NDWI',
  ndre: 'NDRE',
  lai: 'LAI',
};

export const VEGETATION_INDEX_DESCRIPTIONS: Record<string, string> = {
  ndvi: 'Normalized Difference Vegetation Index',
  evi: 'Enhanced Vegetation Index',
  savi: 'Soil-Adjusted Vegetation Index',
  ndwi: 'Normalized Difference Water Index',
  ndre: 'Normalized Difference Red Edge',
  lai: 'Leaf Area Index',
};

// ─── Crop Type Labels ─────────────────────────────────────────────────────────

export const CROP_TYPE_LABELS: Record<string, string> = {
  corn: 'Corn',
  soybean: 'Soybean',
  wheat: 'Wheat',
  cotton: 'Cotton',
  rice: 'Rice',
  sorghum: 'Sorghum',
  barley: 'Barley',
  canola: 'Canola',
  sunflower: 'Sunflower',
  alfalfa: 'Alfalfa',
  potato: 'Potato',
  tomato: 'Tomato',
  grape: 'Grape',
  apple: 'Apple',
  citrus: 'Citrus',
  other: 'Other',
};

export const ASSET_TYPE_LABELS: Record<AssetType, string> = {
  field: 'Field',
  parcel: 'Parcel',
  facility: 'Facility',
  equipment: 'Equipment',
  other: 'Other',
};

// ─── Health Score Thresholds ──────────────────────────────────────────────────

export const HEALTH_THRESHOLDS = {
  excellent: 80,
  good: 60,
  fair: 40,
  poor: 0,
} as const;

// ─── Pagination ───────────────────────────────────────────────────────────────

export const DEFAULT_PAGE_SIZE = 25;
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;

// ─── Plan Tier Labels ─────────────────────────────────────────────────────────

export const PLAN_TIER_LABELS: Record<string, string> = {
  starter: 'Starter',
  growth: 'Growth',
  government: 'Government',
};

// ─── Alert Severity Colors ────────────────────────────────────────────────────

export const ALERT_SEVERITY_COLORS: Record<string, string> = {
  critical: 'text-red-600 bg-red-50 border-red-200',
  warning: 'text-amber-600 bg-amber-50 border-amber-200',
  info: 'text-blue-600 bg-blue-50 border-blue-200',
};
